import React from 'react'

import { Table } from 'antd'

import i18n from 'live/services/i18n'

import { DeleteEntityButton } from './components/DeleteEntityButton'
import { ActionsColumnButtons } from './components/ActionsColumnButtons'

type EditableTableProps = Parameters<typeof Table>[0]
type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

type SelectOptionsArray = (SelectOptions | RestrictedSelectOptions)[]
type DynamicSelectOptions = (record: any) => { label: string; value: ValueUom | string }[]

const toEditableColumns = (
    columns: (ColumnTypes[number] & {
        editable?: boolean
        dataIndex: string
        options?: SelectOptionsArray | DynamicSelectOptions
    })[],
    onEditSave: (row: any) => void,
    localErrors: LocalErrorsTypes[]
) => {
    return columns.map(col => {
        return {
            ...col,
            onCell: record => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                ...col,
                options: typeof col.options === 'function' ? col.options(record) : col.options,
                onEditSave,
                localErrors
            })
        }
    })
}

export const wellColumns = (
    handleDelete: (entity: WellDto) => void,
    onEditSave: (row: any) => void,
    wellOptions: SelectOptions[],
    rigOptions: SelectOptions[],
    optionsOfWellEntity: WellSelectOptions,
    localErrors: LocalErrorsTypes[]
): (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] => {
    const columns = [
        {
            title: 'UUID',
            dataIndex: 'uuid',
            cellType: 'text',
            editable: false,
            render: (value: string, record: WellDto) => {
                return {
                    props: {
                        style: { width: 50 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Well Name'),
            dataIndex: 'wellName',
            cellType: 'select',
            options: wellOptions ?? [],
            allOptions: optionsOfWellEntity,
            editable: true
        },
        {
            title: i18n('Well ID'),
            dataIndex: 'wellId',
            cellType: 'disabledField',
            editable: false,
            render: (value: string, record: WellDto) => {
                return {
                    props: {
                        style: { width: 45 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Rig Name'),
            dataIndex: 'rigName',
            cellType: 'select',
            options: rigOptions ?? [],
            editable: true
        },
        {
            title: i18n('Rig ID'),
            dataIndex: 'rigId',
            cellType: 'disabledField',
            editable: false,
            render: (value: string, record: WellDto) => {
                return {
                    props: {
                        style: { width: 45 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Field'),
            dataIndex: 'fieldName',
            editable: true,
            render: (value: string, record: WellDto) => {
                return {
                    props: {
                        style: { width: 125 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Water Depth'),
            dataIndex: 'waterDepth',
            cellType: 'num',
            editable: true,
            render: (value: number, record: WellDto) => {
                return {
                    props: {
                        style: { width: 70 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Type'),
            dataIndex: 'wellType',
            cellType: 'select',
            options: optionsOfWellEntity?.wellType ?? [],
            editable: true
        },
        {
            title: i18n('Category'),
            dataIndex: 'wellCategory',
            cellType: 'select',
            options: optionsOfWellEntity?.wellCategory ?? [],
            editable: true
        },
        {
            title: i18n('Reference System'),
            dataIndex: 'referenceSystem',
            cellType: 'select',
            options: optionsOfWellEntity?.referenceSystem ?? [],
            editable: true
        },
        {
            title: i18n('Latitude'),
            dataIndex: 'latitude',
            cellType: 'num',
            editable: true,
            render: (value: string, record: WellDto) => {
                return {
                    props: {
                        style: { width: 70 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Longitude'),
            dataIndex: 'longitude',
            cellType: 'num',
            editable: true,
            render: (value: string, record: WellDto) => {
                return {
                    props: {
                        style: { width: 70 }
                    },
                    children: value
                }
            }
        },
        {
            dataIndex: 'actions',
            cellType: 'action',
            render: (_, record: WellDto) => {
                return {
                    props: {
                        style: { width: 50 }
                    },
                    children: <DeleteEntityButton entity={record} entityName={'well'} handleDelete={handleDelete} />
                }
            }
        }
    ]

    return toEditableColumns(columns, onEditSave, localErrors) as (ColumnTypes[number] & {
        editable?: boolean
        dataIndex: string
    })[]
}

export const interventionColumns = (
    handleDelete: (entity: InterventionsDto) => void,
    onEditSave: (row: any) => void,
    optionsOfInterventionEntity: InterventionSelectOptions,
    handleCalendarElementStartDate: (row: any) => JSX.Element,
    handleCalendarElementEndDate: (row: any) => JSX.Element,
    moveEntityToAnotherParent: (entity: InterventionsDto, selectedParent: number) => void,
    wells: WellDto[],
    localErrors: LocalErrorsTypes[]
): (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] => {
    const columns = [
        {
            title: 'UUID',
            dataIndex: 'uuid',
            cellType: 'text',
            editable: false,
            render: (value: string, record: any) => {
                return {
                    props: {
                        style: { width: 250 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Name'),
            dataIndex: 'name',
            editable: true,
            render: (value: string, record: InterventionsDto) => {
                return {
                    props: {
                        style: { width: 200 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Tactic'),
            dataIndex: 'tactic',
            cellType: 'select',
            options: optionsOfInterventionEntity?.tatic ?? [],
            editable: true
        },
        {
            title: i18n('Responsible'),
            dataIndex: 'responsible',
            editable: true,
            render: (value: string, record: InterventionsDto) => {
                return {
                    props: {
                        style: { width: 200 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('startKey'),
            dataIndex: 'start',
            cellType: 'calendar',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementStartDate(record)
                }
            }
        },
        {
            title: i18n('End'),
            dataIndex: 'end',
            cellType: 'calendar',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementEndDate(record)
                }
            }
        },
        {
            dataIndex: 'actions',
            cellType: 'action',
            render: (_, record: InterventionsDto) => {
                return {
                    props: {
                        style: { width: 50 }
                    },
                    children: (
                        <ActionsColumnButtons
                            entity={record}
                            possibleParents={wells}
                            parentName="well"
                            entityName={'intervention'}
                            moveEntityToAnotherParent={moveEntityToAnotherParent}
                            handleDelete={handleDelete}
                        />
                    )
                }
            }
        }
    ]

    return toEditableColumns(columns, onEditSave, localErrors) as (ColumnTypes[number] & {
        editable?: boolean
        dataIndex: string
    })[]
}

export const sectionColumns = (
    handleDelete: (entity: SectionDto) => void,
    onEditSave: (row: any) => void,
    optionsOfSectionEntity: SectionSelectOptions,
    handleCalendarElementStartDate: (row: any) => JSX.Element,
    handleCalendarElementEndDate: (row: any) => JSX.Element,
    moveEntityToAnotherParent: (entity: InterventionsDto, selectedParent: number) => void,
    interventions: InterventionsDto[],
    localErrors: LocalErrorsTypes[]
): (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] => {
    const columns = [
        {
            title: 'UUID',
            dataIndex: 'uuid',
            cellType: 'text',
            editable: false,
            render: (value: string, record: any) => {
                return {
                    props: {
                        style: { width: 250 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Name'),
            dataIndex: 'name',
            editable: true,
            render: (value: string, record: SectionDto) => {
                return {
                    props: {
                        style: { width: 200 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Riser'),
            dataIndex: 'riser',
            cellType: 'select',
            options: optionsOfSectionEntity?.riser ?? [],
            editable: true
        },
        {
            title: i18n('Type'),
            dataIndex: 'type',
            cellType: 'select',
            options: optionsOfSectionEntity?.type ?? [],
            editable: true
        },
        {
            title: i18n('Start MD'),
            dataIndex: 'startMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: SectionDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Final MD'),
            dataIndex: 'finalMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: SectionDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('startKey'),
            dataIndex: 'start',
            cellType: 'calendar',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementStartDate(record)
                }
            }
        },
        {
            title: i18n('End'),
            dataIndex: 'end',
            cellType: 'calendar',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementEndDate(record)
                }
            }
        },
        {
            title: i18n('OD'),
            dataIndex: 'oD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: SectionDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Prev Casing OD'),
            dataIndex: 'prevCasingOD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: SectionDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Prev Casing Final MD'),
            dataIndex: 'prevCasingFinalMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: SectionDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            dataIndex: 'actions',
            cellType: 'action',
            render: (_, record: SectionDto) => {
                return {
                    props: {
                        style: { width: 50 }
                    },
                    children: (
                        <ActionsColumnButtons
                            entity={record}
                            possibleParents={interventions}
                            parentName="intervention"
                            entityName={'section'}
                            moveEntityToAnotherParent={moveEntityToAnotherParent}
                            handleDelete={handleDelete}
                        />
                    )
                }
            }
        }
    ]

    return toEditableColumns(columns, onEditSave, localErrors) as (ColumnTypes[number] & {
        editable?: boolean
        dataIndex: string
    })[]
}

export const rigTableColumns = (
    handleDelete: (entity: RigTableDto) => void,
    onEditSave: (row: any) => void,
    optionsOfRigTableEntity: RigTableSelectOptions,
    handleCalendarElementStartDate: (row: any) => JSX.Element,
    handleCalendarElementEndDate: (row: any) => JSX.Element,
    moveEntityToAnotherParent: (entity: InterventionsDto, selectedParent: number) => void,
    interventions: InterventionsDto[],
    localErrors: LocalErrorsTypes[]
): (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] => {
    const columns = [
        {
            title: 'UUID',
            dataIndex: 'uuid',
            cellType: 'text',
            editable: false,
            render: (value: string, record: any) => {
                return {
                    props: {
                        style: { width: 250 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Type'),
            dataIndex: 'type',
            cellType: 'disabledField',
            options: optionsOfRigTableEntity?.type ?? [],
            editable: false,
            render: (value: string, record: RigTableDto) => {
                return {
                    props: {
                        style: { width: 70 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('RPI Path Status'),
            dataIndex: 'status',
            cellType: 'select',
            options: optionsOfRigTableEntity?.statusType ?? [],
            editable: true
        },
        {
            title: i18n('startKey'),
            dataIndex: 'start',
            cellType: 'calendar',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementStartDate(record)
                }
            }
        },
        {
            title: i18n('End'),
            dataIndex: 'end',
            cellType: 'calendar',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementEndDate(record)
                }
            }
        },
        {
            dataIndex: 'actions',
            cellType: 'action',
            render: (_, record: RigTableDto) => {
                return {
                    props: {
                        style: { width: 50 }
                    },
                    children: (
                        <ActionsColumnButtons
                            entity={record}
                            possibleParents={interventions}
                            parentName="intervention"
                            entityName={'rig table'}
                            moveEntityToAnotherParent={moveEntityToAnotherParent}
                            handleDelete={handleDelete}
                        />
                    )
                }
            }
        }
    ]

    return toEditableColumns(columns, onEditSave, localErrors) as (ColumnTypes[number] & {
        editable?: boolean
        dataIndex: string
    })[]
}

export const runColumns = (
    handleDelete: (entity: RunDto) => void,
    onEditSave: (row: any) => void,
    optionsOfRunEntity: RunSelectOptions,
    handleCalendarElementStartDate: (row: any) => JSX.Element,
    handleCalendarElementEndDate: (row: any) => JSX.Element,
    handleRadioButtonElement: (row: any, columnName: string) => JSX.Element,
    moveEntityToAnotherParent: (entity: InterventionsDto, selectedParent: number) => void,
    sections: SectionDto[],
    localErrors: LocalErrorsTypes[]
): (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] => {
    const columns = [
        {
            title: 'UUID',
            dataIndex: 'uuid',
            cellType: 'text',
            editable: false,
            render: (value: string, record: any) => {
                return {
                    props: {
                        style: { width: 250 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Number'),
            dataIndex: 'number',
            cellType: 'num',
            editable: true,
            render: (value: number, record: RunDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Type'),
            dataIndex: 'type',
            cellType: 'select',
            options: optionsOfRunEntity?.type ?? [],
            editable: true
        },
        {
            title: i18n('Start MD'),
            dataIndex: 'startMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: RunDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Final MD'),
            dataIndex: 'finalMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: RunDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('startKey'),
            dataIndex: 'start',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementStartDate(record)
                }
            }
        },
        {
            title: i18n('End'),
            dataIndex: 'end',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementEndDate(record)
                }
            }
        },
        {
            title: i18n('Tubular Interval Per Time'),
            dataIndex: 'tubularsIntervalPerTime',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 77 }
                    },
                    children: handleRadioButtonElement(record, 'tubularsIntervalPerTime')
                }
            }
        },
        {
            title: i18n('Extend Last Tubular'),
            dataIndex: 'extendLastTubular',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 77 }
                    },
                    children: handleRadioButtonElement(record, 'extendLastTubular')
                }
            }
        },
        {
            dataIndex: 'actions',
            cellType: 'action',
            render: (_, record: RunDto) => {
                return {
                    props: {
                        style: { width: 50 }
                    },
                    children: (
                        <ActionsColumnButtons
                            entity={record}
                            possibleParents={sections}
                            parentName="section"
                            entityName={'run'}
                            moveEntityToAnotherParent={moveEntityToAnotherParent}
                            handleDelete={handleDelete}
                        />
                    )
                }
            }
        }
    ]

    return toEditableColumns(columns, onEditSave, localErrors) as (ColumnTypes[number] & {
        editable?: boolean
        dataIndex: string
    })[]
}

export const cementColumns = (
    handleDelete: (entity: CementedPlugDto) => void,
    onEditSave: (row: any) => void,
    moveEntityToAnotherParent: (entity: InterventionsDto, selectedParent: number) => void,
    runs: RunDto[],
    localErrors: LocalErrorsTypes[]
): (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] => {
    const columns = [
        {
            title: 'UUID',
            dataIndex: 'uuid',
            cellType: 'text',
            editable: false,
            render: (value: string, record: any) => {
                return {
                    props: {
                        style: { width: 250 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Start MD'),
            dataIndex: 'startMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: CementedPlugDto) => {
                return {
                    children: value
                }
            }
        },
        {
            title: i18n('Final MD'),
            dataIndex: 'finalMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: CementedPlugDto) => {
                return {
                    children: value
                }
            }
        },
        {
            dataIndex: 'actions',
            cellType: 'action',
            render: (_, record: CementedPlugDto) => {
                return {
                    props: {
                        style: { width: 50 }
                    },
                    children: (
                        <ActionsColumnButtons
                            entity={record}
                            possibleParents={runs}
                            parentName="run"
                            entityName={'cement plug'}
                            moveEntityToAnotherParent={moveEntityToAnotherParent}
                            handleDelete={handleDelete}
                        />
                    )
                }
            }
        }
    ]

    return toEditableColumns(columns, onEditSave, localErrors) as (ColumnTypes[number] & {
        editable?: boolean
        dataIndex: string
    })[]
}

export const tubularTypeColumns = (
    handleDelete: (entity: TubularTypeDto) => void,
    onEditSave: (row: any) => void,
    optionsOfTubularTypeEntity: TubularTypeSelectOptions,
    handleCalendarElementStartDate: (row: any) => JSX.Element,
    handleCalendarElementEndDate: (row: any) => JSX.Element,
    handleRadioButtonElement: (row: any) => JSX.Element,
    moveEntityToAnotherParent: (entity: InterventionsDto, selectedParent: number) => void,
    runs: RunDto[],
    localErrors: LocalErrorsTypes[]
): (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] => {
    const columns = [
        {
            title: 'UUID',
            dataIndex: 'uuid',
            cellType: 'text',
            editable: false,
            render: (value: string, record: any) => {
                return {
                    props: {
                        style: { width: 250 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Tubular Type'),
            dataIndex: 'tubularType',
            cellType: 'select',
            options: optionsOfTubularTypeEntity?.tubularType ?? [],
            editable: true
        },
        {
            title: i18n('Tubular Connection Type'),
            dataIndex: 'connectionType',
            cellType: 'select',
            options: optionsOfTubularTypeEntity?.connectionType ?? [],
            editable: true
        },
        {
            title: i18n('Permanent Tubular Installation'),
            dataIndex: 'permanentTubularInstallation',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 77 }
                    },
                    children: handleRadioButtonElement(record)
                }
            }
        },
        {
            title: i18n('OD'),
            dataIndex: 'oD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: TubularTypeDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Start MD'),
            dataIndex: 'startMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: TubularTypeDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Final MD'),
            dataIndex: 'finalMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: TubularTypeDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('startKey'),
            dataIndex: 'start',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementStartDate(record)
                }
            }
        },
        {
            title: i18n('End'),
            dataIndex: 'end',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementEndDate(record)
                }
            }
        },
        {
            title: i18n('Third Party Responsible'),
            dataIndex: 'thirdPartyResponsible',
            editable: true,
            render: (value, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 200 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Material Type'),
            dataIndex: 'materialType',
            cellType: 'select',
            options: optionsOfTubularTypeEntity?.materialType ?? [],
            editable: true
        },
        {
            dataIndex: 'actions',
            cellType: 'action',
            render: (_, record: TubularTypeDto) => {
                return {
                    props: {
                        style: { width: 50 }
                    },
                    children: (
                        <ActionsColumnButtons
                            entity={record}
                            possibleParents={runs}
                            parentName="run"
                            entityName={'tubular type'}
                            moveEntityToAnotherParent={moveEntityToAnotherParent}
                            handleDelete={handleDelete}
                        />
                    )
                }
            }
        }
    ]

    return toEditableColumns(columns, onEditSave, localErrors) as (ColumnTypes[number] & {
        editable?: boolean
        dataIndex: string
    })[]
}

export const pipeMovementColumns = (
    handleDelete: (entity: TubularRestrictedPipeMovementsDto) => void,
    onEditSave: (row: any) => void,
    optionsOfTubularTypeEntity: TubularTypeSelectOptions,
    moveEntityToAnotherParent: (entity: TubularRestrictedPipeMovementsDto, selectedParent: number) => void,
    tubularsType: TubularTypeDto[],
    handleCalendarElementStartDate: (row: any) => JSX.Element,
    handleCalendarElementEndDate: (row: any) => JSX.Element,
    localErrors: LocalErrorsTypes[],
    speedyOptionsByRow: Record<number, { label: string; value: ValueUom | string }[]>
): (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] => {
    const columns = [
        {
            title: i18n('Restricted Pipe Movement Type'),
            dataIndex: 'restrictedPipeMovementType',
            cellType: 'select',
            options: optionsOfTubularTypeEntity?.restrictedPipeMovementType ?? [],
            editable: true
        },
        {
            title: i18n('Restricted Movement Goal'),
            dataIndex: 'restrictedPipeMovementSpeed',
            cellType: 'select',
            options: (record: TubularRestrictedPipeMovementsDto) => speedyOptionsByRow[record.rowId] ?? [],
            editable: true
        },
        {
            title: i18n('Start MD'),
            dataIndex: 'startMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: TubularRestrictedPipeMovementsDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Final MD'),
            dataIndex: 'finalMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: TubularRestrictedPipeMovementsDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('startKey'),
            dataIndex: 'start',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementStartDate(record)
                }
            }
        },
        {
            title: i18n('End'),
            dataIndex: 'end',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementEndDate(record)
                }
            }
        },
        {
            dataIndex: 'actions',
            cellType: 'action',
            render: (_, record: TubularRestrictedPipeMovementsDto) => {
                return {
                    props: {
                        style: { width: 50 }
                    },
                    children: (
                        <ActionsColumnButtons
                            entity={record}
                            possibleParents={tubularsType}
                            parentName="tubular type"
                            entityName={'restricted pipe movement'}
                            moveEntityToAnotherParent={moveEntityToAnotherParent}
                            handleDelete={handleDelete}
                        />
                    )
                }
            }
        }
    ]

    return toEditableColumns(columns, onEditSave, localErrors) as (ColumnTypes[number] & {
        editable?: boolean
        dataIndex: string
    })[]
}

export const connectionColumns = (
    handleDelete: (entity: TubularRestrictedConnectionDto) => void,
    onEditSave: (row: any) => void,
    optionsOfTubularTypeEntity: TubularTypeSelectOptions,
    moveEntityToAnotherParent: (entity: InterventionsDto, selectedParent: number) => void,
    tubularsType: TubularTypeDto[],
    handleCalendarElementStartDate: (row: any) => JSX.Element,
    handleCalendarElementEndDate: (row: any) => JSX.Element,
    localErrors: LocalErrorsTypes[],
    timeOptionsByRow: Record<number, { label: string; value: ValueUom | string }[]>
): (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] => {
    const columns = [
        {
            title: i18n('Restriction Type'),
            dataIndex: 'restrictionType',
            cellType: 'select',
            options: optionsOfTubularTypeEntity?.restrictedConnectionType ?? [],
            editable: true
        },
        {
            title: i18n('Restricted Connection Goal'),
            dataIndex: 'restrictedTimeConnection',
            cellType: 'select',
            options: (record: TubularRestrictedConnectionDto) => timeOptionsByRow[record.rowId] ?? [],
            editable: true
        },
        {
            title: i18n('Start MD'),
            dataIndex: 'startMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: TubularRestrictedConnectionDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Final MD'),
            dataIndex: 'finalMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: TubularRestrictedConnectionDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('startKey'),
            dataIndex: 'start',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementStartDate(record)
                }
            }
        },
        {
            title: i18n('End'),
            dataIndex: 'end',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementEndDate(record)
                }
            }
        },
        {
            dataIndex: 'actions',
            cellType: 'action',
            render: (_, record: TubularRestrictedConnectionDto) => {
                return {
                    props: {
                        style: { width: 50 }
                    },
                    children: (
                        <ActionsColumnButtons
                            entity={record}
                            possibleParents={tubularsType}
                            parentName="tubular type"
                            entityName={'restricted connection'}
                            moveEntityToAnotherParent={moveEntityToAnotherParent}
                            handleDelete={handleDelete}
                        />
                    )
                }
            }
        }
    ]

    return toEditableColumns(columns, onEditSave, localErrors) as (ColumnTypes[number] & {
        editable?: boolean
        dataIndex: string
    })[]
}

export const ropColumns = (
    handleDelete: (entity: TubularRestrictedRopDto) => void,
    onEditSave: (row: any) => void,
    optionsOfTubularTypeEntity: TubularTypeSelectOptions,
    moveEntityToAnotherParent: (entity: TubularRestrictedRopDto, selectedParent: number) => void,
    tubularsType: TubularTypeDto[],
    handleCalendarElementStartDate: (row: any) => JSX.Element,
    handleCalendarElementEndDate: (row: any) => JSX.Element,
    localErrors: LocalErrorsTypes[],
    ropGoalOptionsByRow: Record<number, { label: string; value: ValueUom | string }[]>
): (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] => {
    const columns = [
        {
            title: i18n('Restricted ROP'),
            dataIndex: 'restrictedRopType',
            cellType: 'select',
            options: optionsOfTubularTypeEntity?.restrictedRopType ?? [],
            editable: true
        },
        {
            title: i18n('Restricted ROP Goal'),
            dataIndex: 'restrictedRopSpeed',
            cellType: 'select',
            options: (record: TubularRestrictedRopDto) => ropGoalOptionsByRow[record.rowId] ?? [],
            editable: true
        },
        {
            title: i18n('Start MD'),
            dataIndex: 'startMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: TubularRestrictedRopDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('Final MD'),
            dataIndex: 'finalMD',
            cellType: 'num',
            editable: true,
            render: (value: number, record: TubularRestrictedRopDto) => {
                return {
                    props: {
                        style: { width: 100 }
                    },
                    children: value
                }
            }
        },
        {
            title: i18n('startKey'),
            dataIndex: 'start',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementStartDate(record)
                }
            }
        },
        {
            title: i18n('End'),
            dataIndex: 'end',
            render: (_, record: { key: React.Key }) => {
                return {
                    props: {
                        style: { width: 150 }
                    },
                    children: handleCalendarElementEndDate(record)
                }
            }
        },
        {
            dataIndex: 'actions',
            cellType: 'action',
            render: (_, record: TubularRestrictedRopDto) => {
                return {
                    props: {
                        style: { width: 50 }
                    },
                    children: (
                        <ActionsColumnButtons
                            entity={record}
                            possibleParents={tubularsType}
                            parentName="tubular type"
                            entityName={'restricted ROP'}
                            moveEntityToAnotherParent={moveEntityToAnotherParent}
                            handleDelete={handleDelete}
                        />
                    )
                }
            }
        }
    ]

    return toEditableColumns(columns, onEditSave, localErrors) as (ColumnTypes[number] & {
        editable?: boolean
        dataIndex: string
    })[]
}
